import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { examsService } from '@/services/exams.service';
import { coursesService } from '@/services/courses.service';
import { Save, X } from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';

export function CourseForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    course_id: '',
    name: '',
    provider: 'FK Partners',
    modality: 'EAD' as 'EAD' | 'Hybrid' | 'In-Person' | 'Self-Paced',
    total_hours: 40,
    language: 'pt-BR',
    description: '',
    linked_exams: ['CPA'] as string[],
  });

  const { data: exams } = useQuery({
    queryKey: ['exams'],
    queryFn: async () => {
      try {
        return await examsService.getExams();
      } catch (error) {
        console.error('Failed to fetch exams:', error);
        return [];
      }
    },
  });

  const { data: course } = useQuery({
    queryKey: ['course', id],
    queryFn: async () => {
      try {
        return await coursesService.getCourseById(id!);
      } catch (error) {
        console.error('Failed to fetch course:', error);
        return null;
      }
    },
    enabled: isEditing,
  });

  useEffect(() => {
    if (course) {
      setFormData({
        course_id: course.course_id,
        name: course.name,
        provider: course.provider,
        modality: course.modality,
        total_hours: course.total_hours || 40,
        language: course.language,
        description: course.description || '',
        linked_exams: course.linked_exams || [],
      });
    }
  }, [course]);

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return await coursesService.createCourse(data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['courses'] });
      showToast('success', 'Course Created', `Course "${data.name}" has been created successfully.`);
      navigate('/courses');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Failed to create course';
      showToast('error', 'Creation Failed', message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return await coursesService.updateCourse(id!, data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['courses'] });
      queryClient.invalidateQueries({ queryKey: ['course', id] });
      showToast('success', 'Course Updated', `Course "${data.name}" has been updated successfully.`);
      navigate('/courses');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Failed to update course';
      showToast('error', 'Update Failed', message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isEditing) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditing ? 'Edit Course' : 'Create Course'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="card space-y-6">
        {/* Basic Information */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Course ID *
              </label>
              <input
                type="text"
                required
                value={formData.course_id}
                onChange={(e) => handleChange('course_id', e.target.value)}
                disabled={isEditing}
                className="input-field"
                placeholder="CPA_PREP"
              />
              {isEditing && (
                <p className="text-xs text-gray-500 mt-1">Course ID cannot be changed</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Provider *
              </label>
              <select
                required
                value={formData.provider}
                onChange={(e) => handleChange('provider', e.target.value)}
                className="input-field"
              >
                <option>FK Partners</option>
                <option>ANBIMA Educação</option>
                <option>Top Invest</option>
                <option>XP Educação</option>
                <option>Planejar</option>
                <option>ANCORD</option>
                <option>B3 Educação</option>
                <option>APIMEC</option>
                <option>PREVIC</option>
                <option>GARP</option>
                <option>Kaplan Schweser</option>
                <option>CFA Institute</option>
                <option>Mark Meldrum</option>
                <option>CAIA Association</option>
                <option>Upper Mark</option>
              </select>
            </div>
          </div>
        </div>

        {/* Course Details */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Course Details</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Course Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="input-field"
                placeholder="Curso Preparatório CPA"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Modality *
                </label>
                <select
                  required
                  value={formData.modality}
                  onChange={(e) => handleChange('modality', e.target.value)}
                  className="input-field"
                >
                  <option value="EAD">EAD</option>
                  <option value="Hybrid">Hybrid</option>
                  <option value="In-Person">In-Person</option>
                  <option value="Self-Paced">Self-Paced</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total Hours *
                </label>
                <input
                  type="number"
                  required
                  min="0"
                  value={formData.total_hours}
                  onChange={(e) => handleChange('total_hours', parseInt(e.target.value))}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Language *
                </label>
                <select
                  required
                  value={formData.language}
                  onChange={(e) => handleChange('language', e.target.value)}
                  className="input-field"
                >
                  <option value="pt-BR">Portuguese (Brazil)</option>
                  <option value="en-US">English (US)</option>
                  <option value="es-ES">Spanish</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                rows={4}
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                className="input-field"
                placeholder="Comprehensive course description..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Linked Exams *
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Select one or more exams that this course prepares students for
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto p-1">
                {(Array.isArray(exams) ? exams : (exams as any)?.exams || []).map((exam: any) => {
                  const isSelected = formData.linked_exams.includes(exam.exam_id);
                  return (
                    <label
                      key={exam.exam_id}
                      className={`relative flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-md ${
                        isSelected
                          ? 'border-primary-500 bg-primary-50 shadow-sm'
                          : 'border-gray-200 bg-white hover:border-primary-300'
                      }`}
                    >
                      <div className="flex items-center h-5">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              handleChange('linked_exams', [...formData.linked_exams, exam.exam_id]);
                            } else {
                              handleChange('linked_exams', formData.linked_exams.filter(id => id !== exam.exam_id));
                            }
                          }}
                          className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                        />
                      </div>
                      <div className="ml-3 flex-1 min-w-0">
                        <span className={`block text-sm font-semibold ${
                          isSelected ? 'text-primary-900' : 'text-gray-900'
                        }`}>
                          {exam.exam_id}
                        </span>
                        <span className={`block text-xs mt-1 ${
                          isSelected ? 'text-primary-700' : 'text-gray-500'
                        }`}>
                          {exam.name}
                        </span>
                      </div>
                      {isSelected && (
                        <div className="absolute top-2 right-2">
                          <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </label>
                  );
                })}
              </div>
              {formData.linked_exams.length > 0 && (
                <p className="text-sm text-gray-600 mt-3">
                  <span className="font-semibold text-primary-600">{formData.linked_exams.length}</span> exam{formData.linked_exams.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t">
          <button
            type="button"
            onClick={() => navigate('/courses')}
            className="btn-secondary flex items-center"
          >
            <X className="w-4 h-4 mr-2" />
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="btn-primary flex items-center"
          >
            <Save className="w-4 h-4 mr-2" />
            {isPending ? 'Saving...' : isEditing ? 'Update Course' : 'Create Course'}
          </button>
        </div>
      </form>
    </div>
  );
}
